import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { createSlasCallbackUrl } from '~/features/checkout/utils/createSlasCallbackUrl';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { extractPaymentInstrumentByMethod } from '~/services/altapay/lib/utils';
import { PaymentMethod } from '~/shared/constants';
import { useLocalStorage } from '~/shared/hooks/useLocalStorage';
import { useSetPaymentInstrument } from './useSetPaymentInstrument';

export const useDefaultOrderFlow = (options?: Omit<UseMutationOptions, 'mutationFn'>) => {
    const { createOrder: createOrderFromBasket } = useBasket();
    const { setPaymentInstrument } = useSetPaymentInstrument();

    // Used by Nets callback page for payment processing.
    const [, setPaymentInstrumentKey] = useLocalStorage<string | undefined>('paymentInstrumentKey');
    const [, setPaymentMethodKey] = useLocalStorage<string | undefined>('paymentMethodKey');

    const { mutateAsync: createOrder, isPending: isCreatingOrder } = useMutation({
        ...options,
        mutationFn: async () => {
            const newOrder = await createOrderFromBasket();

            if (!newOrder) throw newOrder;

            return newOrder;
        },
    });

    const generateDefaultOrderFlowLinks = async () => {
        const newOrder = await createOrder();

        const orderPaymentInstrument = extractPaymentInstrumentByMethod(
            newOrder.paymentInstruments,
            PaymentMethod.NETS,
        );

        const cancellationUrl = createSlasCallbackUrl({
            orderNo: newOrder.orderNo,
            responseCode: 'cancel',
        });

        const callbackUrl =
            newOrder.c_redirectURL ||
            createSlasCallbackUrl({ orderNo: newOrder.orderNo, responseCode: 'ok' });

        setPaymentInstrumentKey(orderPaymentInstrument?.paymentInstrumentId);
        setPaymentMethodKey(orderPaymentInstrument?.paymentMethodId);

        return { callbackUrl, cancellationUrl };
    };

    const startDefaultOrderFlow = async () => {
        const { callbackUrl, cancellationUrl } = await generateDefaultOrderFlowLinks();

        // Going back from the Nets payment page will then cancel the transaction.
        window.history.replaceState(null, '', cancellationUrl);

        return callbackUrl;
    };

    const startFreeOrderFlow = async () => {
        // Free orders need no payment instrument. Setting Nets removes AltaPay, which would cause errors.
        await setPaymentInstrument({ paymentMethod: PaymentMethod.NETS });

        return startDefaultOrderFlow();
    };

    return {
        startDefaultOrderFlow,
        startFreeOrderFlow,
        isGeneratingOrderFlowLinks: isCreatingOrder,
    };
};
